// Imports
import React, { useEffect, useRef, useState } from 'react';
import Select from '../../../../../../../shareables/foundation/front-end/components/forms/fields/select';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import APIForm, { APIFormOptions } from '../../../../../../../shareables/integrations/api/components/form';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetReplacementAdminsResponse } from '../../../../../../../shareables/types/api/main/website/member/settings/replacement-admins/get';
import { DeleteAdminParameters } from '../../../../../../../shareables/types/api/main/website/member/settings/admin/delete';
import styled from 'styled-components/macro';
import commonActions from '../../../../../../../shareables/foundation/front-end/redux/actions';
import { useNavigate, Navigate, useOutletContext } from 'react-router-dom';
import memberHasPrivilege from '../../../../../../../shareables/utils/member-has-privilege';
import { darken } from 'polished';
import theme from '../../../../../../../shareables/theme';
import selfActions from '../../../../../../../redux/self/actions';


// Styled components
const Container = styled.div`
	line-height: 1.3125rem;
`;

const FormWrapper = styled.div`
	margin-top: 1.5625rem;
	
	&& form {
		button[type='submit'] {
			background-color: ${theme.colors.danger};
			
			&:hover:not(:disabled):not([data-disabled]),
			&:focus:not(:disabled):not([data-disabled]) {
				background-color: ${darken(0.05, theme.colors.danger)} !important;
			}
		}
	}
`;

const OnlyAdminWithReplacementsNote = styled.p`
	margin-top: 1.5625rem;
`;


// Define the accepted props
interface Props {
	storybookReplacementAdmins?: GetReplacementAdminsResponse;
	storybookNoContext?: true;
}


// Function component
const Admin: React.FC<Props> = ({ storybookReplacementAdmins, storybookNoContext }) => {
	// Use state
	const [replacementAdmins, setReplacementAdmins] = useState<GetReplacementAdminsResponse | null>(
		storybookReplacementAdmins ?? null
	);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	const member = useAppSelector((state) => state.self.member);
	const memberPrivileges = useAppSelector((state) => state.self.member?.privileges);
	const websiteID = useAppSelector((state) => state.self.website?.id);
	
	
	// Use ref
	const ref = useRef<HTMLDivElement | null>(null);
	
	
	// Use React Router functionality
	const navigate = useNavigate();
	const updateHeight: (newHeight: number) => void = useOutletContext();
	
	
	// Update height for the members settings page
	useEffect(() => {
		if (!(storybookReplacementAdmins || storybookNoContext) && ref.current) {
			updateHeight(ref.current.clientHeight);
		}
	}, [updateHeight, replacementAdmins, storybookReplacementAdmins, storybookNoContext]);
	
	
	// Initialize
	const isFullAdmin = memberHasPrivilege('*', memberPrivileges ?? []);
	
	
	// Load replacement options on mount
	useEffect(() => {
		if (isFullAdmin) {
			void dispatch(
				apiActions.call<undefined, undefined, GetReplacementAdminsResponse>({
					action: 'GET',
					uri: `/website/${window.location.hostname}/member/settings/replacement-admins`,
					completion: (json) => {
						setReplacementAdmins(json.response);
					},
				})
			);
		}
	}, [dispatch, isFullAdmin]);
	
	
	// Handle missing privileges
	if (!memberPrivileges) {
		return <Navigate to='../general' />;
	}
	
	
	// If loading, then display nothing
	if (isFullAdmin && replacementAdmins === null) {
		return null;
	}
	
	
	// Build form options
	const formOptions: APIFormOptions<DeleteAdminParameters, undefined, undefined> = {
		action: 'DELETE',
		uri: `/website/${window.location.hostname}/member/settings/admin`,
		warning: {
			button: 'Yes, step down',
			message: 'You cannot undo this yourself.',
		},
		submitButton: {
			text: 'Step down',
			color: 'danger',
		},
	};
	
	
	// Handle completion
	const handleCompletion = () => {
		// Display success message
		dispatch(
			commonActions.showPageMessage({
				color: 'success',
				title: 'Success',
				message: 'You’ve successfully stepped down from being an administrator.',
			})
		);
		
		
		// Remove privileges
		if (member) {
			dispatch(selfActions.storeMember({ ...member, privileges: undefined }));
		}
		
		
		// Navigate user away from admin page
		navigate('../general');
	};
	
	
	// Initialize
	const onlyAdminNoReplacements = isFullAdmin && replacementAdmins?.replacementAdmins?.length === 0;
	const onlyAdminWithReplacements = isFullAdmin && (replacementAdmins?.replacementAdmins?.length ?? 0) > 0;
	
	
	// Return JSX
	return (
		<Container ref={ref}>
			<p>
				You currently have administrative privileges granting you the ability to manage{' '}
				{isFullAdmin ? 'all aspects' : 'part'} of this community website. If you would no longer like to have these
				privileges,{' '}
				{onlyAdminNoReplacements ? (
					<React.Fragment>
						because you are the only available member of this community website, you’ll need to{' '}
						<a
							href={`https://admin.${process.env.REACT_APP__ENVIRONMENT_HOSTNAME}/settings/miscellaneous/disable-and-delete?websiteID=${websiteID ?? ''}`}
						>
							delete this website
						</a>{' '}
						instead.
					</React.Fragment>
				) : (
					'you can step down using the button below.'
				)}
			</p>
			
			{onlyAdminWithReplacements && (
				<OnlyAdminWithReplacementsNote>
					Because you are the only administrator with full privileges, you must select another member to transfer your
					administrative privileges to before stepping down.
				</OnlyAdminWithReplacementsNote>
			)}
			
			{!onlyAdminNoReplacements && (
				<FormWrapper>
					<APIForm options={formOptions} onCompletion={handleCompletion}>
						{onlyAdminWithReplacements && (
							<Select required name='newAdminID' placeholder='Please select…' label='New administrator'>
								{replacementAdmins?.replacementAdmins?.map((member) => (
									<option value={member.id} key={member.id}>
										{member.name}
									</option>
								))}
							</Select>
						)}
					</APIForm>
				</FormWrapper>
			)}
		</Container>
	);
};

export default Admin;
